"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.UnavWorkMinutesInclusionSection = void 0;
const Dropdown_1 = require("@app/component/ui/dropdown/Dropdown");
const dropdown_input_1 = require("@app/component/ui/forms/dropdown-input/dropdown-input");
const form_grid_layout_1 = require("@app/component/ui/forms/form-grid-layout");
const time_input_1 = require("@app/component/ui/input/time-input/time-input");
const graphql_1 = require("@app/gql-types/graphql");
const store_1 = require("@app/store");
const util_1 = require("@app/util");
const use_workspace_unavailability_categories_1 = require("@app/util/hooks/use-workspace-unavailability-categories");
const useWorkspace_1 = require("@app/util/hooks/useWorkspace");
const user_option_parser_1 = require("@app/util/user-option-parser");
const react_1 = __importDefault(require("react"));
const react_hook_form_1 = require("react-hook-form");
const unavailability_context_1 = require("../unavailability-context");
const use_get_shifts_work_minutes_1 = require("../util/use-get-shifts-work-minutes");
const modal_section_1 = require("./modal-section");
const UnavWorkMinutesInclusionSection = () => {
    const { formControl, isEmployeeCalendar, canEdit } = (0, unavailability_context_1.useUnavailability)();
    const workspace = (0, useWorkspace_1.useWorkspace)();
    const userOptions = (0, store_1.useRootSelector)((state) => state.me.options?.filter(util_1.isValue) ?? []);
    const userOptionsParser = react_1.default.useMemo(() => new user_option_parser_1.UserOptionParser(userOptions), [userOptions]);
    const employees = (0, store_1.useRootSelector)((state) => state.employees);
    const unavCategories = (0, use_workspace_unavailability_categories_1.getWorkspaceUnavailabilityCategories)(workspace);
    const workMinutesByOptions = [
        util_1.miscUtil.getFilteredContentForOrganization('time_off_edit_include_hour_option_contract', {
            label: (0, util_1.t)('BY_CONTRACT'),
            value: graphql_1.TimeOffWorkMinutesBy.Contract,
        }, {}),
        {
            label: (0, util_1.t)('BY_REPLACED_SHIFT'),
            value: graphql_1.TimeOffWorkMinutesBy.Shift,
        },
        {
            label: (0, util_1.t)('CUSTOM'),
            value: graphql_1.TimeOffWorkMinutesBy.Custom,
        },
    ].filter(util_1.isValue);
    const timeOffWorkMinutesInclusionOptions = [
        {
            label: (0, util_1.t)('DAYS_WITH_PLANNED_SHIFT'),
            value: graphql_1.TimeOffWorkMinutesInclusion.ShiftDays,
        },
        {
            label: (0, util_1.t)('EVERY_WORK_DAY'),
            value: graphql_1.TimeOffWorkMinutesInclusion.WorkDays,
        },
        {
            label: (0, util_1.t)('EVERY_DAY'),
            value: graphql_1.TimeOffWorkMinutesInclusion.EveryDay,
        },
    ];
    const workMinutesController = (0, react_hook_form_1.useController)({
        control: formControl,
        name: 'workMinutes',
    });
    const _userId = (0, react_hook_form_1.useWatch)({ control: formControl, name: 'user' });
    const _contractId = (0, react_hook_form_1.useWatch)({ control: formControl, name: 'contractId' });
    const _selectedCategoryId = (0, react_hook_form_1.useWatch)({ control: formControl, name: 'categoryId' });
    const _workMinutesBy = (0, react_hook_form_1.useWatch)({ control: formControl, name: 'workMinutesBy' });
    const _workMinutes = (0, react_hook_form_1.useWatch)({ control: formControl, name: 'workMinutes' });
    const _period = (0, react_hook_form_1.useWatch)({
        control: formControl,
        name: 'period',
    });
    const _start = _period?.start;
    const _end = _period?.end;
    const isMultipleDays = react_1.default.useMemo(() => (0, util_1.isValue)(_start) && (0, util_1.isValue)(_end) ? _end.diff(_start, 'days') >= 1 : false, [_period]);
    const selectedCategory = unavCategories.find((con) => con.id === _selectedCategoryId);
    const selectedEmployee = (0, util_1.isValue)(_userId) ? employees[_userId] : undefined;
    const desiredContract = selectedEmployee?.contracts?.find((con) => con.id === _contractId);
    const workMinutesTotalShifts = (0, use_get_shifts_work_minutes_1.useGetShiftsWorkMinutes)();
    const decideCounted = (_workMinutesByOverride) => {
        const contractOptions = desiredContract?.options;
        const contractAmount = contractOptions?.minutesOfWorkPerShift / 60 || 0;
        const decideWorkMinutesBy = _workMinutesByOverride ?? _workMinutesBy;
        if (decideWorkMinutesBy === graphql_1.TimeOffWorkMinutesBy.Settings) {
            if (selectedCategory?.maxDailyMinutes) {
                return selectedCategory?.maxDailyMinutes / 60;
            }
            else {
                return contractAmount;
            }
        }
        if (decideWorkMinutesBy === graphql_1.TimeOffWorkMinutesBy.Contract) {
            return contractAmount;
        }
        if (decideWorkMinutesBy === graphql_1.TimeOffWorkMinutesBy.Shift) {
            return workMinutesTotalShifts / 60;
        }
        if (decideWorkMinutesBy === graphql_1.TimeOffWorkMinutesBy.Custom) {
            return (userOptionsParser.getNumber('timeOffCustomInclusion') ?? 0) / 60;
        }
        return 0;
    };
    const _workMinutesInclusionController = (0, react_hook_form_1.useController)({
        control: formControl,
        name: 'workMinutesInclusion',
    });
    const _workMinutesByController = (0, react_hook_form_1.useController)({
        control: formControl,
        name: 'workMinutesBy',
    });
    const canManagerCalendarEdit = !isEmployeeCalendar && canEdit;
    return (react_1.default.createElement(modal_section_1.ModalSection, { id: 'work-minutes-inclusion', init: () => {
            workMinutesController.field.onChange(decideCounted() * 60);
        }, cleanup: () => {
            const timeOffDefaultMinutesInclusion = userOptionsParser.getEnum('timeOffDefaultInclusion', graphql_1.TimeOffWorkMinutesInclusion);
            const timeOffDefaultWorkMinutesBy = userOptionsParser.getEnum('timeOffWorkMinutesBy', graphql_1.TimeOffWorkMinutesBy);
            _workMinutesInclusionController.field.onChange(timeOffDefaultMinutesInclusion ?? graphql_1.TimeOffWorkMinutesInclusion.WorkDays);
            _workMinutesByController.field.onChange(timeOffDefaultWorkMinutesBy ?? graphql_1.TimeOffWorkMinutesBy.Shift);
            workMinutesController.field.onChange(undefined);
        } },
        react_1.default.createElement(form_grid_layout_1.FormGridLayout, null,
            react_1.default.createElement(dropdown_input_1.DropdownInput, { options: timeOffWorkMinutesInclusionOptions, label: (0, util_1.t)('UNAV_WORK_MINUTES_INCLUSION'), control: formControl, name: 'workMinutesInclusion', disabled: !isMultipleDays || isEmployeeCalendar || !canManagerCalendarEdit }),
            react_1.default.createElement("div", null),
            react_1.default.createElement(Dropdown_1.Dropdown, { type: 'variable', size: 'full-width', style: 'light', singleSelect: true, options: workMinutesByOptions, label: (0, util_1.t)('INCLUDE'), disabled: isEmployeeCalendar || !canManagerCalendarEdit, value: workMinutesByOptions.find((opt) => opt.value === _workMinutesBy), onChange: (v) => {
                    _workMinutesByController.field.onChange(v.value);
                    workMinutesController.field.onChange(decideCounted(v.value) * 60);
                } }),
            react_1.default.createElement(time_input_1.TimeInput, { label: (0, util_1.t)('VALUE'), 
                // disabled={_workMinutesBy !== 'custom' || disabledEditation}
                disabled: _workMinutesBy !== graphql_1.TimeOffWorkMinutesBy.Custom ||
                    isEmployeeCalendar ||
                    !canManagerCalendarEdit, value: _workMinutesBy === graphql_1.TimeOffWorkMinutesBy.Custom ||
                    (_workMinutesBy === graphql_1.TimeOffWorkMinutesBy.Shift &&
                        (0, util_1.isDefined)(_workMinutes))
                    ? (_workMinutes ?? 0)
                    : decideCounted() * 60, onChange: workMinutesController.field.onChange }))));
};
exports.UnavWorkMinutesInclusionSection = UnavWorkMinutesInclusionSection;
